import {
  useRef,
  useEffect,
  useContext,
  createContext,
  useCallback,
  useMemo,
} from "react";
import { ApolloClient, useApolloClient } from "@apollo/client";
import { UseBoundStore, StoreApi } from "zustand";
import type {
  TemplateParams,
  TemplateParamsContextType,
  TemplateStore,
} from "../types";
import { useCurrentUser } from "../../../utils/hooks";
import { createTemplateStore } from "./store";
import {
  selectFieldFromCacheMemoized,
  selectDraftFieldFromCacheMemoized,
  selectIsPersistingFieldMemoized,
  selectMetadata,
} from "./selectors";
import * as types from "_graphql-types-frontend/graphql";
import { Field, Metadata } from "../types";

// Enhanced store management with proper cleanup
interface StoreManager {
  store: ReturnType<typeof createTemplateStore>;
  refCount: number;
}

const templateStores: Record<string, StoreManager> = {};

function createStoreKey(params: TemplateParamsContextType): string {
  // Include editMode in the key to ensure separate stores for edit vs non-edit modes.
  // Include the reporting period so switching periods gets its own store + re-fetch.
  const periodKey = params.period
    ? `${params.period.periodType}:${params.period.asOfDate}`
    : "";
  return `${params.ownerId}-${params.ownerType}-${params.name}-${params.editMode}-${periodKey}`;
}

export function useTemplateStore(
  params: TemplateParams,
  client: ApolloClient<any>,
  currentUser: ReturnType<typeof useCurrentUser>
) {
  const storeKey = createStoreKey({
    name: params.name,
    ownerId: params.ownerId,
    ownerType: params.ownerType,
    editMode: params.editMode,
    period: params.period,
  });

  // Initialize store if it doesn't exist
  if (!templateStores[storeKey]) {
    templateStores[storeKey] = {
      store: createTemplateStore({
        params,
        client,
        currentUser,
      }),
      refCount: 0,
    };
  }

  const storeRef = templateStores[storeKey];

  useEffect(() => {
    // Increment ref count on mount
    storeRef.refCount++;

    return () => {
      // Decrement ref count on unmount
      storeRef.refCount--;

      if (storeRef.refCount < 0) {
        console.warn(
          `Ref count for store ${storeKey} is negative: ${storeRef.refCount}`
        );
      }

      // Use setTimeout to delay cleanup, allowing for quick remounts
      if (storeRef.refCount <= 0) {
        setTimeout(() => {
          if (
            templateStores[storeKey] &&
            templateStores[storeKey].refCount <= 0
          ) {
            templateStores[storeKey].store.getState().cleanup();
            delete templateStores[storeKey];
          }
        }, 1000);
      }
    };
  }, [storeKey]); // Remove storeRef from dependencies to avoid recreation

  return storeRef.store;
}

// Cleanup utility for removing unused stores
export function releaseTemplateStore(params: TemplateParamsContextType) {
  const storeKey = createStoreKey(params);
  const storeManager = templateStores[storeKey];

  if (storeManager) {
    storeManager.refCount--;

    if (storeManager.refCount <= 0) {
      // Cleanup the store
      storeManager.store.getState().cleanup();
      delete templateStores[storeKey];
    }
  }
}

export function useTemplate(
  params: TemplateParams
): UseBoundStore<StoreApi<TemplateStore>> {
  const client = useApolloClient();
  const currentUser = useCurrentUser();
  const store = useTemplateStore(params, client, currentUser);
  const cleanupRef = useRef<(() => void) | null>(null);
  const currentStoreKeyRef = useRef<string | null>(null);

  // Create store key for comparison
  const storeKey = createStoreKey({
    name: params.name,
    ownerId: params.ownerId,
    ownerType: params.ownerType,
    editMode: params.editMode,
    period: params.period,
  });

  // Initialize when store key changes (params that affect store identity)
  useEffect(() => {
    // Check if this is the same store key as before
    if (currentStoreKeyRef.current === storeKey) return;

    let isMounted = true;
    currentStoreKeyRef.current = storeKey;

    const init = async () => {
      if (isMounted) {
        try {
          // Cleanup previous store's initialization if it exists
          if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
          }

          // Initialize the new store
          cleanupRef.current = await store.getState().initialize();
        } catch (error) {
          console.error("Failed to initialize template store:", error);
        }
      }
    };

    init();

    return () => {
      isMounted = false;
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      currentStoreKeyRef.current = null;
    };
  }, [storeKey, store]); // Re-run when store key changes (param changes that affect store identity)

  return store;
}

// Context for template parameters
export const TemplateParamsContext =
  createContext<TemplateParamsContextType | null>(null);

export function useTemplateParams(): TemplateParamsContextType {
  const context = useContext(TemplateParamsContext);
  if (!context) {
    throw new Error(
      "useTemplateParams must be used within a TemplateComponent"
    );
  }
  return context;
}

// Hook for accessing store from context
export function useTemplateStoreFromContext() {
  const params = useTemplateParams();

  const storeKey = createStoreKey({
    name: params.name,
    ownerId: params.ownerId,
    ownerType: params.ownerType,
    editMode: params.editMode,
    period: params.period,
  });

  const storeManager = templateStores[storeKey];

  if (!storeManager) {
    throw new Error(
      `Template store not found for key: ${storeKey}. Make sure the template is properly initialized before accessing it from context.`
    );
  }

  return storeManager.store;
}

// Utility function to manually cleanup all stores (useful for testing or app shutdown)
export function cleanupAllTemplateStores() {
  Object.values(templateStores).forEach(storeManager => {
    storeManager.store.getState().cleanup();
  });
  Object.keys(templateStores).forEach(key => {
    delete templateStores[key];
  });
}

/**
 * Hook for reading a field from cache with optional state specification
 * @returns A function that reads a field from cache
 */
export function useReadFieldFromCache() {
  const store = useTemplateStoreFromContext();
  const currentUser = useCurrentUser();

  // Subscribe to store state changes
  const storeState = store(state => state);

  return useCallback(
    (
      key: string,
      state?: types.FieldState.RangerGenerated | types.FieldState.Mgr | null
    ) => selectFieldFromCacheMemoized(storeState, key, state, currentUser),
    [storeState, currentUser]
  );
}

/**
 * Hook for reading a specific field with memoization of the result
 * @param key Field key in the format "ownerType.fieldKey"
 * @param state Optional state to specify which version of the field to read
 * @returns The field and its metadata
 */
export function useFieldValue(
  key: string,
  state?: types.FieldState.RangerGenerated | types.FieldState.Mgr | null
) {
  const store = useTemplateStoreFromContext();
  const currentUser = useCurrentUser();

  return store(
    useCallback(
      (storeState: TemplateStore) =>
        selectFieldFromCacheMemoized(storeState, key, state, currentUser),
      [key, state, currentUser?.flags.subscriberDataAccess]
    )
  );
}

/**
 * Hook for reading a draft field from cache
 * @returns A function that reads a draft field from cache
 */
export function useReadDraftFieldFromCache() {
  const store = useTemplateStoreFromContext();

  // Subscribe to store state changes
  const storeState = store(state => state);

  return useCallback(
    (key: string) => selectDraftFieldFromCacheMemoized(storeState, key),
    [storeState]
  );
}

/**
 * Hook for reading a specific draft field with memoization of the result
 * @param key Field key in the format "ownerType.fieldKey"
 * @returns The draft field and its metadata
 */
export function useDraftFieldValue(key: string) {
  const store = useTemplateStoreFromContext();

  return store(
    useCallback(
      (storeState: TemplateStore) =>
        selectDraftFieldFromCacheMemoized(storeState, key),
      [key]
    )
  );
}

/**
 * Hook to check if a field is currently being persisted
 * @returns A function that checks if a field is persisting
 */
export function useIsPersistingField() {
  const store = useTemplateStoreFromContext();

  // Subscribe to store state changes
  const storeState = store(state => state);

  return useCallback(
    (field: Field) => selectIsPersistingFieldMemoized(storeState, field),
    [storeState]
  );
}

export function useGetMetadata() {
  const store = useTemplateStoreFromContext();

  // Subscribe only to the fieldMeta portion
  const fieldMeta = store(state => state.templateData?.fieldMeta);

  return useCallback(
    (fieldKey: string) => {
      return selectMetadata({ templateData: { fieldMeta } }, fieldKey);
    },
    [fieldMeta] // Only re-creates when fieldMeta changes
  );
}

/**
 * Hook to get metadata for a specific field with memoization
 */
export function useMetadata(fieldKey: string) {
  const store = useTemplateStoreFromContext();

  // This subscribes and will only re-render when the specific metadata changes
  return store(state => selectMetadata(state, fieldKey));
}
